/**
 * Time Formatting Utilities for Messaging
 * 
 * Formats timestamps in iMessage-style:
 * - Just now, 2m ago, 1h ago (within today)
 * - Yesterday
 * - Mon, Tue, Wed (this week)
 * - 1/5, 12/25 (older)
 */

import { isToday, isYesterday, isThisWeek, format } from 'date-fns';
import { isStudioDesign } from '@/lib/design';
import { toDate } from '@/utils/timeFormat';

/**
 * Format a timestamp for the conversation list
 * Shows relative time for recent, then day name, then date
 */
export function formatConversationTime(timestamp) {
  if (!timestamp) return '';
  
  const date = toDate(timestamp);
  if (!date) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  // Within the last minute
  if (diffMins < 1) {
    return 'Just now';
  }

  // Within the last hour
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }

  // Within today
  if (isToday(date)) {
    if (diffHours < 12) {
      return `${diffHours}h ago`;
    }
    return format(date, 'h:mm a');
  }

  // Yesterday
  if (isYesterday(date)) {
    return 'Yesterday';
  }

  // This week - show day name
  if (isThisWeek(date, { weekStartsOn: 0 })) {
    return format(date, 'EEE'); // Mon, Tue, etc.
  }

  // Older - show date. Studio writes dates as "Sep 5" (101 §7); classic keeps 9/5.
  return format(date, isStudioDesign() ? 'MMM d' : 'M/d');
}

/**
 * Format a timestamp for message display
 * Shows time for today, date + time for older
 */
export function formatMessageTime(timestamp) {
  if (!timestamp) return '';
  
  const date = toDate(timestamp);
  if (!date) return '';

  if (isToday(date)) {
    return format(date, 'h:mm a');
  }

  if (isYesterday(date)) {
    return `Yesterday ${format(date, 'h:mm a')}`;
  }

  if (isThisWeek(date, { weekStartsOn: 0 })) {
    return format(date, 'EEE h:mm a');
  }

  return format(date, isStudioDesign() ? 'MMM d, yyyy · h:mm a' : 'M/d/yy h:mm a');
}

/**
 * Format a date for message date separators
 */
export function formatDateSeparator(timestamp) {
  if (!timestamp) return '';
  
  const date = toDate(timestamp);
  if (!date) return '';

  if (isToday(date)) {
    return 'Today';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  if (isThisWeek(date, { weekStartsOn: 0 })) {
    return format(date, 'EEEE'); // Monday, Tuesday, etc.
  }

  return format(date, 'MMMM d, yyyy'); // January 5, 2026
}

/**
 * Check if two timestamps are on different days
 */
export function isDifferentDay(timestamp1, timestamp2) {
  if (!timestamp1 || !timestamp2) return true;
  
  const date1 = toDate(timestamp1);
  const date2 = toDate(timestamp2);
  if (!date1 || !date2) return true;

  return date1.toDateString() !== date2.toDateString();
}

/**
 * Check if two messages should be grouped together
 * (same sender, within 2 minutes). System messages (centered pills like
 * "X added Y" / "X left the group") are rendered standalone and must NEVER
 * participate in sender grouping — they carry the actor's senderId in
 * Firestore, so without this guard a real message followed by a system
 * event within 2 min would be marked `isLastInGroup: false` and its read
 * receipt (Read / Read by N / Delivered) would be hidden.
 */
export function shouldGroupMessages(message1, message2) {
  if (!message1 || !message2) return false;
  if (message1.type === 'system' || message2.type === 'system') return false;
  if (message1.senderId !== message2.senderId) return false;
  
  const time1 = toDate(message1.createdAt);
  const time2 = toDate(message2.createdAt);
  if (!time1 || !time2) return false;
  
  const diffMs = Math.abs(time2 - time1);
  const twoMinutes = 2 * 60 * 1000;
  
  return diffMs < twoMinutes;
}
